import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { clearPortalAuth, getPortalUser } from "@/lib/portalAuth";
import {
  LayoutDashboard, Building2, FileText, Calendar, FolderUp,
  FileSignature, Receipt, UserCircle, LogOut, Menu, X, Sparkles, Plus, ChevronsUpDown, Check,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3a2b13f5-84ee-42a0-a8c4-4ac266af26a7/artifacts/8jhfiwyn_BESSONSOLUTION-logo-avatar.png";

export const PORTAL_SECTIONS = [
  {
    title: "Pilotage",
    items: [
      { key: "dashboard",   icon: LayoutDashboard, label: "Tableau de bord" },
      { key: "companies",   icon: Building2,       label: "Mes entreprises" },
    ],
  },
  {
    title: "Projets & échanges",
    items: [
      { key: "projects",    icon: FileText,        label: "Mes projets" },
      { key: "documents",   icon: FolderUp,        label: "Documents partagés" },
      { key: "appointments", icon: Calendar,       label: "Rendez-vous" },
    ],
  },
  {
    title: "Commercial",
    items: [
      { key: "proposals",   icon: FileSignature,   label: "Devis & propositions" },
      { key: "invoices",    icon: Receipt,         label: "Factures" },
    ],
  },
  {
    title: "Compte",
    items: [
      { key: "account",     icon: UserCircle,      label: "Mon compte" },
    ],
  },
];

export default function PortalSidebar({ active, onChange, counts = {}, onAddCompany, companies = [], activeCompanyId = "all", onCompanyChange = () => {} }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(getPortalUser());
  const [picker, setPicker] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    api.get("/portal/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPicker(false); };
    if (picker) window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [picker]);

  const logout = () => { clearPortalAuth(); nav("/portail/connexion"); };

  const firstName = (me?.full_name || "").split(" ")[0] || me?.email?.split("@")[0] || "Entrepreneur";

  const select = (key) => { onChange(key); setOpen(false); };
  const pickCompany = (cid) => { onCompanyChange(cid); setPicker(false); };

  const activeCompany = companies.find((c) => c.id === activeCompanyId);
  const hasMulti = companies.length > 1;

  return (
    <>
      {/* Mobile top bar (portal only) */}
      <header className="lg:hidden relative z-20 grid grid-cols-3 items-center p-3 bg-white/90 backdrop-blur-md border-b border-black/8 sticky top-0" data-testid="portal-mobile-bar">
        <div className="flex justify-start">
          <button onClick={() => setOpen((o) => !o)} className="p-2 rounded-xl hover:bg-black/5 transition-colors" data-testid="portal-mobile-menu" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        <div className="flex justify-center">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="B" className="w-9 h-9 rounded-xl shadow-md ring-2 ring-[#06B6D4]/30" />
            <span className="display font-black text-base text-[#0A1128] leading-none">space</span>
          </Link>
        </div>
        <div className="flex justify-end">
          <button onClick={logout} className="p-2 rounded-xl hover:bg-black/5 text-[#0A1128]" data-testid="portal-mobile-logout" aria-label="Déconnexion">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <aside
        className={`${open ? "fixed inset-0 z-50 lg:inset-auto" : "hidden lg:block"} lg:fixed lg:top-0 lg:left-0 lg:z-30 lg:w-72 lg:h-screen overflow-hidden`}
        data-testid="portal-sidebar"
      >
        {/* Mobile backdrop */}
        {open && <div className="lg:hidden absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />}
        {/* Drawer panel */}
        <div className={`absolute top-0 left-0 h-screen w-72 max-w-[85vw] overflow-hidden flex flex-col`}>
        {/* Background — lighter, less navy */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1E293B] via-[#1E2A44] to-[#1E293B]" />
        <div className="absolute -top-32 -right-20 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{ background: "#06B6D4", filter: "blur(80px)" }} />
        <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full opacity-15 pointer-events-none" style={{ background: "#A7F3D0", filter: "blur(90px)" }} />

        <div className="relative h-full flex flex-col text-white p-5 overflow-y-auto">
          {/* Logo + brand */}
          <Link to="/" className="hidden lg:flex items-center gap-3 pb-4 border-b border-white/10 group">
            <img src={LOGO_URL} className="w-11 h-11 rounded-xl shadow-lg group-hover:scale-105 transition-transform" alt="BESSON" />
            <div>
              <div className="display font-black text-base leading-none">BESSON</div>
              <div className="display font-black text-base leading-none text-[#67E8F9] -mt-0.5">SOLUTIONS</div>
              <div className="text-[9px] tracking-[0.32em] text-white/55 uppercase mt-1">Mon Espace</div>
            </div>
          </Link>

          {/* Company switcher — visible when 1+ company */}
          {companies.length > 0 && (
            <div className="mt-4 relative" ref={pickerRef}>
              <div className="text-[10px] font-bold tracking-[0.28em] uppercase text-white/45 mb-1.5 px-1">Entreprise active</div>
              <button
                type="button"
                onClick={() => hasMulti && setPicker((p) => !p)}
                data-testid="portal-company-switcher"
                className={`w-full flex items-center gap-2 p-2 rounded-xl border border-white/10 bg-white/8 hover:bg-white/12 transition-colors ${hasMulti ? "cursor-pointer" : "cursor-default"}`}
              >
                {activeCompanyId === "all" ? (
                  <>
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-[#06B6D4] font-extrabold text-[11px]">All</span>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-[11px] text-white/55 leading-none">Toutes les entreprises</div>
                      <div className="text-sm font-bold leading-tight">{companies.length} {companies.length > 1 ? "entreprises" : "entreprise"}</div>
                    </div>
                  </>
                ) : (
                  <>
                    {activeCompany?.logo_url ? (
                      <img src={activeCompany.logo_url} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-[#06B6D4]/35" />
                    ) : (
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#06B6D4] text-[#0A1128] font-extrabold text-sm">{(activeCompany?.name || "?").slice(0, 1).toUpperCase()}</span>
                    )}
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-[10px] text-white/55 uppercase tracking-widest leading-none">{activeCompany?.sector || "Entreprise"}</div>
                      <div className="text-sm font-bold leading-tight truncate">{activeCompany?.name}</div>
                    </div>
                  </>
                )}
                {hasMulti && <ChevronsUpDown size={14} className="text-white/55 shrink-0" />}
              </button>

              {picker && hasMulti && (
                <div className="absolute left-0 right-0 mt-1 bg-white rounded-xl shadow-2xl border border-black/8 overflow-hidden z-40" data-testid="portal-company-picker">
                  <button onClick={() => pickCompany("all")} data-testid="portal-company-pick-all" className={`w-full flex items-center gap-2 p-2.5 hover:bg-[#FAFAFA] text-left transition-colors ${activeCompanyId === "all" ? "bg-[#06B6D4]/8" : ""}`}>
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0A1128] text-[#06B6D4] font-extrabold text-[11px]">All</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-[#0A1128]">Toutes les entreprises</div>
                      <div className="text-[10px] text-[#64748B]">Vue agrégée</div>
                    </div>
                    {activeCompanyId === "all" && <Check size={14} className="text-[#06B6D4]" />}
                  </button>
                  <div className="border-t border-black/5 max-h-72 overflow-auto">
                    {companies.map((c) => (
                      <button key={c.id} onClick={() => pickCompany(c.id)} data-testid={`portal-company-pick-${c.id}`} className={`w-full flex items-center gap-2 p-2.5 hover:bg-[#FAFAFA] text-left transition-colors ${activeCompanyId === c.id ? "bg-[#06B6D4]/8" : ""}`}>
                        {c.logo_url ? (
                          <img src={c.logo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0A1128] text-[#06B6D4] font-extrabold text-[11px]">{(c.name || "?").slice(0, 1).toUpperCase()}</span>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-[#0A1128] truncate">{c.name}</div>
                          {c.sector && <div className="text-[10px] text-[#64748B] truncate">{c.sector}</div>}
                        </div>
                        {activeCompanyId === c.id && <Check size={14} className="text-[#06B6D4]" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quick actions */}
          <div className="mt-3">
            <button onClick={onAddCompany} data-testid="portal-quick-add-company" className="w-full flex items-center justify-center gap-1 px-2 py-2 rounded-xl bg-white/8 hover:bg-white/15 border border-white/10 text-[11px] font-bold text-white transition-colors">
              <Plus size={12} className="text-[#06B6D4]" /> Ajouter une entreprise
            </button>
          </div>

          <nav className="mt-5 flex-1 space-y-5">
            {PORTAL_SECTIONS.map((g) => (
              <div key={g.title}>
                <div className="text-[10px] font-bold tracking-[0.32em] text-white/40 uppercase px-3 mb-2">{g.title}</div>
                <div className="space-y-0.5">
                  {g.items.map((it) => {
                    const isActive = active === it.key;
                    const count = counts[it.key];
                    return (
                      <button
                        key={it.key}
                        type="button"
                        data-testid={`portal-nav-${it.key}`}
                        onClick={() => select(it.key)}
                        className={`group relative w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all ${
                          isActive
                            ? "bg-white text-[#0A1128] shadow-lg shadow-black/20 font-bold"
                            : "text-white/75 hover:bg-white/8 hover:text-white"
                        }`}
                      >
                        {isActive && (
                          <span className="absolute -left-5 top-1/2 -translate-y-1/2 w-1.5 h-7 rounded-r-full bg-[#06B6D4] shadow-[0_0_20px_#06B6D4]" />
                        )}
                        <it.icon size={17} className={isActive ? "text-[#06B6D4]" : "opacity-80 group-hover:opacity-100"} />
                        <span className="truncate flex-1">{it.label}</span>
                        {typeof count === "number" && count > 0 && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? "bg-[#06B6D4]/15 text-[#06B6D4]" : "bg-white/12 text-white/80"}`}>{count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User footer */}
          <div className="mt-5 pt-4 border-t border-white/10">
            <div className="flex items-center gap-3">
              {me?.avatar_url ? (
                <img src={me.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-[#06B6D4]/40" />
              ) : (
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#06B6D4] text-[#0A1128] font-extrabold text-sm">
                  {firstName.slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="flex-1 min-w-0">
                <div className="display text-sm font-bold truncate">{firstName}</div>
                <div className="text-[11px] text-white/55 truncate">{me?.email}</div>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="portal-logout"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-white/8 hover:bg-white/15 border border-white/10 text-white text-[12px] font-bold rounded-xl px-3 py-2.5 transition-colors"
            >
              <LogOut size={13} /> Déconnexion
            </button>
          </div>
        </div>
        </div>
      </aside>
    </>
  );
}
